/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * addressAutocomplete.ts — pluggable address-suggestion contract and demo data.
 * It provides locale-aware fixtures for disconnected development and a stable
 * adapter interface for production geocoders. UI state, map rendering, and
 * persistence belong to the consuming address widget.
 */

import { resolveInitialLocale } from '@/i18n/catalog';

/**
 * E5-S2: Address autocomplete provider adapter. Plan: 14 wireframes (Screen 06).
 * Implementations: MockAddressProvider (demo), future Yandex/2GIS adapter.
 */

export interface AddressSuggestion {
  /** Display label suitable for the current operator locale. */
  label: string;
  /** Value to set in scenario field (full address string) */
  value: string;
  /** Optional coordinates for map pin */
  lat?: number;
  lon?: number;
}

export interface IAddressAutocompleteProvider {
  suggest(query: string): Promise<AddressSuggestion[]>;
}

/** Debounce delay for suggest requests (ms). */
export const SUGGEST_DEBOUNCE_MS = 300;

/** Minimum query length to trigger suggest. */
export const SUGGEST_MIN_LENGTH = 2;

/**
 * Mock provider: returns static locale-aware suggestions without an external API.
 */
export class MockAddressProvider implements IAddressAutocompleteProvider {
  private readonly ruFixtures: AddressSuggestion[] = [
    { label: 'ул. Арбат, 10', value: 'Москва, ул. Арбат, 10', lat: 55.7522, lon: 37.5915 },
    { label: 'ул. Арбат, 12', value: 'Москва, ул. Арбат, 12', lat: 55.7524, lon: 37.5918 },
    { label: 'ул. Арбат (общая)', value: 'Москва, ул. Арбат', lat: 55.7518, lon: 37.5912 },
    { label: 'Москва, Красная пл.', value: 'Москва, Красная площадь', lat: 55.7539, lon: 37.6208 },
    { label: 'Москва, центр', value: 'Москва, центр', lat: 55.7558, lon: 37.6173 },
  ];

  private readonly enFixtures: AddressSuggestion[] = [
    { label: '10 Arbat Street', value: '10 Arbat Street, Moscow', lat: 55.7522, lon: 37.5915 },
    { label: '12 Arbat Street', value: '12 Arbat Street, Moscow', lat: 55.7524, lon: 37.5918 },
    { label: 'Arbat Street', value: 'Arbat Street, Moscow', lat: 55.7518, lon: 37.5912 },
    { label: 'Red Square, Moscow', value: 'Red Square, Moscow', lat: 55.7539, lon: 37.6208 },
    { label: 'Central Moscow', value: 'Central Moscow', lat: 55.7558, lon: 37.6173 },
  ];

  /** Filters the active-locale fixtures by a case-insensitive operator query. */
  async suggest(query: string): Promise<AddressSuggestion[]> {
    const q = query.trim().toLowerCase();
    if (q.length < SUGGEST_MIN_LENGTH) return [];
    const fixtures = resolveInitialLocale() === 'en' ? this.enFixtures : this.ruFixtures;
    return fixtures.filter(
      (f) => f.label.toLowerCase().includes(q) || f.value.toLowerCase().includes(q)
    );
  }
}
